/**
 * A board row's "Today's list" cell (26/10/2026, client: "Show all of these
 * on the board" — user: "he wants to see all the list multiple times based
 * on the used time … L1 L2 L3 L1 L2 L3"): one line per live drop — its time
 * (Danish first) and its list — so a list shows each time it is used; every
 * list group of the container in ONE list by time (26/10/2026, user: "ignore
 * that just show them based on time please on each container"); "+N not used
 * today" when lists of today's sets get no drop. A group whose list gets no
 * drop today keeps the time column ("—") and says why in a short tag — no
 * slot, cut today, paused (26/10/2026, user: "I want every list has the
 * time"). A drop that went out carries a green ✓ after its list (26/10/2026,
 * user: "a complete icon or green next to each list if sent has been
 * complete"), its send time on hover. Hover or focus the cell for today's lists in send order with ×N and
 * the ones not used (19/10/2026); the List group chips beside it still open
 * each group's lists.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { CheckCircle2 } from 'lucide-react';

import { type ClockCtx, dkLabel } from '@/features/drop-generator/utils/clock/dk-label';
import { minutesLabel } from '@/features/drop-generator/utils/format';

import { cphTime } from '../../shared/StateBadges';
import { type BoardGroupRow, noDropWhy } from '../board-list-rows';
import type { DropGenContainerSummary } from '../../../drop-generator.types';
import { containerDayLines } from './container-day-lines';
import { listName, type DropListLine } from './today-list-order';
import { TodayListsHoverCard } from './TodayListsHoverCard';

const LINE = 'flex h-5 min-w-0 items-center gap-1.5';
/** The time column — the same width with or without a time, so the names line up. */
const TIME = 'inline-block min-w-[2.1rem] shrink-0 font-mono text-[10.5px] tabular-nums text-muted-foreground';
type Slots = DropGenContainerSummary['slots'];

const DropLine: FC<{ line: DropListLine; clock: ClockCtx }> = ({ line, clock }) => {
  const dk = dkLabel(line.minute, clock.tz, clock.runDate);
  return (
    <span className={LINE} data-testid="today-drop-line">
      <span className={TIME} title={dk ? `${dk} Danish time · ${clock.flag} ${minutesLabel(line.minute)}` : undefined}>
        {dk ?? minutesLabel(line.minute)}
      </span>
      <span className="min-w-0 truncate" title={listName(line)}>{listName(line)}</span>
      {line.sentAt ? <SentMark at={line.sentAt} /> : null}
    </span>
  );
};

/** The drop went out — green, as "sent" on the Live strip and "done" on the State badge. */
const SentMark: FC<{ at: string }> = ({ at }) => (
  <span role="img" aria-label="sent" title={`Sent 🇩🇰 ${cphTime(at)}`} className="inline-flex shrink-0" data-testid="today-drop-sent">
    <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
  </span>
);

const NoDropLine: FC<{ name: string; why: { tag: string; why: string } }> = ({ name, why }) => (
  <span className={LINE} data-testid="today-no-drop-line">
    <span className={TIME}>—</span>
    <span className="min-w-0 truncate text-muted-foreground" title={name}>{name}</span>
    <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground" title={why.why}>{why.tag}</span>
  </span>
);

export const TodayListsCell: FC<{ groups: BoardGroupRow[]; clock: ClockCtx; slots?: Slots }> = ({ groups, clock, slots = [] }) => {
  const day = containerDayLines(groups);
  return (
    <TodayListsHoverCard label={day.label} entries={day.order} clock={clock}>
      <span
        tabIndex={day.order.length > 0 ? 0 : undefined}
        data-testid="today-list-cell"
        className="flex min-w-0 max-w-full cursor-default flex-col rounded text-xs outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {day.drops.map((line) => <DropLine key={line.key} line={line} clock={clock} />)}
        {day.idle.map(({ group, name }) => (
          <NoDropLine key={group.listGroupId} name={name} why={noDropWhy(group, slots)} />
        ))}
        {day.drops.length + day.idle.length === 0 ? <span className={`${LINE} text-muted-foreground`}>—</span> : null}
        {day.unused > 0 ? (
          <span className={`${LINE} text-[10.5px] text-muted-foreground`} data-testid="lists-today-unused">
            +{day.unused} not used today
          </span>
        ) : null}
      </span>
    </TodayListsHoverCard>
  );
};
